import { Capacitor } from '@capacitor/core';

const LAST_PATH_KEY = 'lastAppPath';
const PICKER_AT_KEY = 'nativeFilePickerAt';
const PICKER_TTL_MS = 30 * 60 * 1000;

export const isNativeApp = () =>
  typeof Capacitor?.isNativePlatform === 'function' && Capacitor.isNativePlatform();

export const markNativeFilePicker = () => {
  try {
    localStorage.setItem(PICKER_AT_KEY, String(Date.now()));
  } catch (_) { /* ignore */ }
};

export const persistAppPath = (pathname, search = '') => {
  if (!pathname || pathname === '/login') return;
  try {
    localStorage.setItem(LAST_PATH_KEY, `${pathname}${search || ''}`);
  } catch (_) { /* ignore */ }
};

export const consumeNativePickerRestorePath = () => {
  try {
    const at = Number(localStorage.getItem(PICKER_AT_KEY) || 0);
    if (!at || Date.now() - at > PICKER_TTL_MS) {
      localStorage.removeItem(PICKER_AT_KEY);
      return null;
    }
    const path = localStorage.getItem(LAST_PATH_KEY);
    localStorage.removeItem(PICKER_AT_KEY);
    return path || null;
  } catch (_) {
    return null;
  }
};

/** Sistem kamerasını açmadan (uygulamadan atmadan) kare yakala */
export const capturePhotoInApp = async () => {
  if (!navigator?.mediaDevices?.getUserMedia) {
    throw new Error('Bu cihazda kamera kullanılamıyor.');
  }

  const stream = await navigator.mediaDevices.getUserMedia({
    video: { facingMode: { ideal: 'environment' } },
    audio: false
  });

  return new Promise((resolve, reject) => {
    const overlay = document.createElement('div');
    overlay.className = 'inapp-camera-overlay';
    overlay.innerHTML = `
      <div class="inapp-camera-box">
        <video playsinline autoplay muted></video>
        <div class="inapp-camera-actions">
          <button type="button" class="inapp-camera-cancel">İptal</button>
          <button type="button" class="inapp-camera-shot">Fotoğraf çek</button>
        </div>
      </div>
    `;
    const style = document.createElement('style');
    style.textContent = `
      .inapp-camera-overlay{position:fixed;inset:0;z-index:100000;background:rgba(15,23,42,.86);display:flex;align-items:center;justify-content:center;padding:16px;}
      .inapp-camera-box{width:min(420px,100%);background:#0f172a;border-radius:14px;overflow:hidden;box-shadow:0 16px 40px rgba(0,0,0,.35);}
      .inapp-camera-box video{width:100%;height:auto;max-height:70vh;background:#000;display:block;}
      .inapp-camera-actions{display:flex;gap:8px;padding:12px;}
      .inapp-camera-actions button{flex:1;border:none;border-radius:8px;padding:10px 12px;font-weight:650;cursor:pointer;}
      .inapp-camera-cancel{background:#334155;color:#fff;}
      .inapp-camera-shot{background:#22c55e;color:#052e16;}
    `;
    document.body.appendChild(style);
    document.body.appendChild(overlay);
    const video = overlay.querySelector('video');
    video.srcObject = stream;

    const cleanup = () => {
      stream.getTracks().forEach((t) => t.stop());
      overlay.remove();
      style.remove();
    };

    overlay.querySelector('.inapp-camera-cancel').onclick = () => {
      cleanup();
      reject(new Error('İptal edildi'));
    };
    overlay.querySelector('.inapp-camera-shot').onclick = () => {
      try {
        const w = video.videoWidth || 1280;
        const h = video.videoHeight || 720;
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(video, 0, 0, w, h);
        canvas.toBlob((blob) => {
          cleanup();
          if (!blob) {
            reject(new Error('Fotoğraf alınamadı'));
            return;
          }
          const file = new File([blob], `foto_${Date.now()}.jpg`, { type: 'image/jpeg' });
          resolve(file);
        }, 'image/jpeg', 0.86);
      } catch (err) {
        cleanup();
        reject(err);
      }
    };
  });
};

/**
 * Uygulama ici kamera bu cihazda kullanilabilir mi?
 * iOS WKWebView'de navigator.mediaDevices tanimsiz olabiliyor. Bu durumda
 * sistem kamerasina dusmuyoruz: sistem kamerasi uygulamayi kapatiyor
 * (eski derlemede Info.plist izin metni yok / bellek baskisi).
 */
export const isInAppCameraSupported = () =>
  typeof navigator !== 'undefined' && !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
